import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiGetBlob, apiPost } from "../api/http";
import { useAuditProgress } from "../hooks/useAuditProgress";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { useActiveSite } from "../context/ActiveSiteContext";
import SimpleUrlInput from "../components/SimpleUrlInput";
import { ScoreRing } from "../components/SeoScoreCard";
import { parseApiError } from "../utils/apiErrors";
import { isValidWebsiteUrl } from "../utils/urlValidation";
import PlanGateBanner from "../components/shared/PlanGateBanner";

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

const DeepAudit = () => {
  const { user } = useAuth();
  const { activeSite } = useActiveSite();
  const { showSuccess, showError, showInfo } = useToast();
  const [url, setUrl] = useState("");
  const [auditId, setAuditId] = useState(null);
  const [downloadError, setDownloadError] = useState("");
  const { progress, subscribe, reset } = useAuditProgress();
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [planGate, setPlanGate] = useState(null);
  const [pdfStatusState, setPdfStatusState] = useState("pending"); // "pending", "success", "failed"
  const [retryingPdf, setRetryingPdf] = useState(false);
  const planName = String(user?.plan_name || "free").toLowerCase();
  const isPremium = ["solo", "agency", "premium"].includes(planName);

  const resultId = progress?.result?.id;

  const handleRetryPdf = async () => {
    const activeResultId = resultId || progress?.result?.id;
    if (!activeResultId) return;

    setRetryingPdf(true);
    setDownloadError("");
    try {
      await apiPost(`/api/audit/${activeResultId}/retry-pdf`);
      showSuccess("PDF generation re-triggered. Please wait...");
      setPdfStatusState("pending");
    } catch (err) {
      showError(parseApiError(err, "Failed to re-trigger PDF generation"));
    } finally {
      setRetryingPdf(false);
    }
  };

  useEffect(() => {
    if (progress?.result) {
      if (progress.result.pdf_ready) {
        setPdfStatusState("success");
      } else {
        setPdfStatusState("pending");
      }
    } else {
      setPdfStatusState("pending");
    }
  }, [progress?.result]);

  useEffect(() => {
    if (pdfStatusState !== "pending" || !resultId) return;

    let pollCount = 0;
    const maxPolls = 20; // 40s max

    const interval = setInterval(async () => {
      pollCount++;
      if (pollCount > maxPolls) {
        setPdfStatusState("failed");
        clearInterval(interval);
        return;
      }

      try {
        const response = await apiGet(`/api/audit/${resultId}`);
        const auditData = response.data?.data;
        if (auditData) {
          if (auditData.pdf_ready) {
            setPdfStatusState("success");
            clearInterval(interval);
          } else if (auditData.audit_data?.pdfStatus === "failed") {
            setPdfStatusState("failed");
            clearInterval(interval);
          }
        }
      } catch (err) {
        // Quietly ignore transient network/server polling errors
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [pdfStatusState, resultId]);

  useEffect(() => {
    if (activeSite) {
      setUrl(activeSite.url);
    }
  }, [activeSite]);

  useEffect(() => {
    if (progress?.done && progress?.result) {
      setLoading(false);
      showSuccess("Deep audit completed. Generating PDF in the background...");
    }
  }, [progress, showSuccess]);

  useEffect(() => {
    if (progress?.error) {
      setLoading(false);
      showError(progress.error);
    }
  }, [progress, showError]);

  const startDeepAudit = async (e) => {
    e.preventDefault();
    if (!url) return;
    if (!isPremium) {
      setPlanGate({
        code: "PLAN_FEATURE_REQUIRED",
        message: "Deep audit is available on Premium plan.",
      });
      showInfo("Upgrade to Premium to run deep audits.");
      return;
    }
    if (!isValidWebsiteUrl(url)) {
      showError("Please enter a valid URL.");
      return;
    }

    setLoading(true);
    setAuditId(null);
    setApiError(null);
    setPlanGate(null);
    setDownloadError("");
    reset();

    try {
      const { data } = await apiPost("/api/audit/run", { websiteUrl: url });
      setAuditId(data.auditId);
      await subscribe(data.auditId, { progressPath: "/api/audit/progress" });
    } catch (err) {
      const apiData = err?.response?.data || {};
      if (apiData?.code === "PLAN_FEATURE_REQUIRED") {
        setPlanGate({
          code: apiData.code,
          message: apiData.cta || apiData.error || "Deep audit is available on Premium plan.",
        });
        showInfo(apiData.error || "Upgrade to Premium to continue.");
      } else {
        setApiError(parseApiError(err, "Failed to start deep audit"));
        showError(parseApiError(err, "Failed to start deep audit"));
      }
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    const resultId = progress?.result?.id;
    if (!resultId) return;

    setDownloadError("");
    try {
      const response = await apiGetBlob(`/api/audit/reports/${resultId}`);
      downloadBlob(response.data, `deep-audit-${resultId}.pdf`);
      showInfo("PDF report download started.");
    } catch (err) {
      const apiData = err?.response?.data || {};
      if (apiData?.code === "PLAN_FEATURE_REQUIRED") {
        setPlanGate({
          code: apiData.code,
          message: apiData.cta || "PDF export is available on Premium plan.",
        });
        showInfo(apiData.error || "Upgrade to Premium to download PDF reports.");
      } else {
        setDownloadError(parseApiError(err, "Failed to download report"));
        showError(parseApiError(err, "Failed to download report"));
      }
    }
  };

  const isComplete = progress?.done || progress?.result;
  const isError = progress?.error;

  return (
    <div className="page">
      <h1 className="page-title">Deep Audit & Report</h1>
      <p style={{ color: "var(--text-muted)", marginBottom: 24 }}>
        Run a deeply comprehensive Lighthouse analysis. When the audit finishes, the PDF is available inside your authenticated dashboard flow.
      </p>
      <div className="card" style={{
        marginBottom: 30,
        position: "relative",
        overflow: "hidden",
        minHeight: !isPremium ? "260px" : "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
      }}>
        {!isPremium && (
          <PlanGateBanner
            title="Premium Deep Audit"
            description="Deep audit is available on Premium plans. Run detailed Lighthouse analyses and export professional PDF reports."
          />
        )}

        <form className="tool-url-form" onSubmit={startDeepAudit} style={{ opacity: !isPremium ? 0.3 : 1, pointerEvents: !isPremium ? "none" : "auto" }}>
          <SimpleUrlInput
            value={url}
            onChange={setUrl}
            disabled={!isPremium || (auditId && !isComplete && !isError)}
            required
            placeholder="your-website.com"
            aria-label="Website URL for deep audit"
          />
          <button
            type="submit"
            className="btn btn-primary tool-url-submit"
            disabled={!url || (auditId && !isComplete && !isError)}
          >
            {auditId && !isComplete && !isError ? "Running..." : "Start Audit"}
          </button>
        </form>
      </div>

      {auditId && !isComplete && !isError ? (
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontWeight: 500 }}>
              {progress?.label || "Initializing..."}
            </span>
            <span style={{ color: "var(--accent)", fontWeight: "bold" }}>
              {Math.round(progress?.percent || 0)}%
            </span>
          </div>

          <div className="progress-bar-track">
            <div
              className="progress-bar-fill"
              style={{ width: `${Math.max(2, progress?.percent || 0)}%` }}
            />
          </div>
          <div className="progress-steps" style={{ marginTop: 8 }}>
            The completed audit is saved to your account even if the live socket disconnects.
          </div>
        </div>
      ) : null}

      {(isError || apiError) ? (
        <div className="card" style={{ borderLeft: "4px solid var(--danger)", backgroundColor: "#fef2f2" }}>
          <h3 style={{ color: "var(--danger)", marginTop: 0 }}>Audit Failed</h3>
          <p>{progress?.error || apiError}</p>
        </div>
      ) : null}

      {/* 
        The planGate card below is removed because the UI is now 
        proactively disabled for non-premium users above. 
      */}

      {isComplete && progress?.result ? (
        <div className="card" style={{ padding: "40px 20px", display: "flex", flexDirection: "column", alignItems: "center", background: "#f8fafc", borderColor: "var(--border-focus)" }}>
          <div style={{ marginBottom: 24, transform: "scale(1.2)" }}>
            <ScoreRing score={progress.result.score || 0} size={120} />
          </div>
          <h2 style={{ margin: "0 0 12px 0", fontSize: "1.75rem", color: "var(--text-heading)", fontWeight: 800, letterSpacing: "-0.02em" }}>Deep Audit Complete</h2>
          <p style={{ color: "var(--text-muted)", textAlign: "center", maxWidth: 440, marginBottom: 32, fontSize: "1.05rem", lineHeight: 1.6 }}>
            We've thoroughly analyzed the domain and your audit score is ready.
          </p>

          {pdfStatusState === "success" ? (
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={handleDownload}
              style={{ fontSize: "1.1rem", padding: "14px 36px", borderRadius: 100, boxShadow: "0 8px 24px -4px rgba(37,99,235,0.3)" }}
            >
              Download PDF Report
            </button>
          ) : pdfStatusState === "pending" ? (
            <button 
              type="button" 
              className="btn btn-primary" 
              disabled
              style={{
                fontSize: "1rem",
                padding: "12px 24px",
                borderRadius: 100,
                opacity: 0.6,
                cursor: "not-allowed",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                whiteSpace: "normal",
                textAlign: "center",
                maxWidth: "100%",
                wordBreak: "break-word",
              }}
            >
              <svg className="animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 20, height: 20, flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeDasharray="30 30" strokeLinecap="round" />
              </svg>
              Generating PDF report in background...
            </button>
          ) : (
            <div style={{ padding: "20px 24px", background: "#fef3c7", borderRadius: 12, border: "1px solid #fcd34d", color: "#92400e", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              <p style={{ margin: 0, fontWeight: 600 }}>Audit finished, but PDF generation failed.</p>
              <p style={{ margin: 0, fontSize: "0.9rem" }}>You can still see your score above. Please try re-triggering the PDF generation below.</p>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleRetryPdf}
                disabled={retryingPdf}
                style={{ fontSize: "1rem", padding: "10px 24px", borderRadius: 8, boxShadow: "0 4px 12px -2px rgba(217,119,6,0.3)", backgroundColor: "#d97706", borderColor: "#d97706" }}
              >
                {retryingPdf ? "Re-triggering..." : "Re-trigger PDF Generation"}
              </button>
            </div>
          )}
          {downloadError ? <div className="progress-error" style={{ marginTop: 16 }}>{downloadError}</div> : null}
        </div>
      ) : null}
    </div>
  );
};

export default DeepAudit;
